import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home, 
  Store, 
  MapPin, 
  CreditCard, 
  BarChart3, 
  Settings,
  Users,
  LogOut,
  ChevronDown,
  ChevronRight,
  Ticket,
  Link as LinkIcon,
  Receipt,
  Palette,
  Activity,
  Edit,
  ClipboardList,
  ClipboardSignature
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { usersService } from '@/services/users.service';
import { auth } from '@/lib/firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword as firebaseUpdatePassword } from 'firebase/auth';
import { toast } from 'sonner';
import { MerchantSwitcher } from '@/components/merchant-switcher';

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  requiresMerchant?: boolean;
  adminOnly?: boolean;
  // Visibility controls
  merchantOnly?: boolean; // visible only when a merchant is selected
  globalOnly?: boolean;   // visible only in global view
  isGroup?: boolean;
  children?: NavItem[];
}

export function Sidebar() {
  const { user, signOut, currentMerchant, isGlobalView, setUser: setAuthUser } = useAuthStore();
  const isBevPassAdmin = user?.role === 'bevpass_admin';
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Define navigation with hierarchical structure
  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, requiresMerchant: false },
    { name: 'Merchants', href: '/merchants', icon: Store, requiresMerchant: false, globalOnly: true },
    { name: 'Waitlists', href: '/waitlists', icon: ClipboardList, requiresMerchant: false, adminOnly: true, globalOnly: true },
    { name: 'Locations', href: '/locations', icon: MapPin, requiresMerchant: true, merchantOnly: true },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, requiresMerchant: false, globalOnly: true },
    {
      name: 'Passes',
      icon: Ticket,
      isGroup: true,
      requiresMerchant: false,
      children: [
        { name: 'Plans & Pricing', href: '/plans', icon: CreditCard, requiresMerchant: true, merchantOnly: true },
        { name: 'Usage & Redemptions', href: '/redemptions', icon: Activity, requiresMerchant: true, merchantOnly: true },
        { name: 'Affiliate Links', href: '/affiliates', icon: LinkIcon, requiresMerchant: false },
        {
          name: 'Merchant Signup Forms',
          href: '/merchant-signup-forms',
          icon: ClipboardSignature,
          requiresMerchant: false,
          adminOnly: true,
          globalOnly: true,
        },
        {
          name: 'Promotions',
          href: '/plans/promotions',
          icon: Ticket,
          requiresMerchant: false,
          adminOnly: true,
          globalOnly: true,
        },
      ],
    },
    { name: 'Users', href: '/users', icon: Users, requiresMerchant: false, adminOnly: true, globalOnly: true },
    { name: 'Users', href: '/users', icon: Users, requiresMerchant: true, merchantOnly: true },
    {
      name: 'Settings',
      icon: Settings,
      isGroup: true,
      requiresMerchant: false,
      children: [
        { name: 'Billing', href: '/settings/billing', icon: Receipt, requiresMerchant: true, merchantOnly: true },
        { name: 'Branding', href: '/settings/branding', icon: Palette, requiresMerchant: true },
      ],
    },
  ];

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => 
      prev.includes(groupName) 
        ? prev.filter(name => name !== groupName)
        : [...prev, groupName]
    );
  };

  const isActiveRoute = (href?: string) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isGroupExpanded = (groupName: string) => {
    return expandedGroups.includes(groupName);
  };

  const hasActiveChild = (children?: NavItem[]) => {
    if (!children) return false;
    return children.some(child => isActiveRoute(child.href));
  };

  // Filter navigation based on role and context
  const filterByContext = (item: NavItem): boolean => {
    // Non-admins can't see admin-only items
    if (item.adminOnly && !isBevPassAdmin) return false;
    
    // Merchant users can't see global-only items ever
    const isMerchantUser = user?.role === 'merchant_owner' || user?.role === 'merchant_viewer';
    if (item.globalOnly && isMerchantUser) return false;
    
    // Global view specific filtering (for admins)
    if (item.globalOnly && !isGlobalView) return false;
    if (item.merchantOnly && isGlobalView) return false;
    if (item.requiresMerchant && (isGlobalView || !currentMerchant)) return false;
    return true;
  };

  const filteredNavigation = navigation
    .map(item => {
      if (item.children) {
        const filteredChildren = item.children.filter(filterByContext);
        if (filteredChildren.length === 0) {
          return { ...item, children: [] } as NavItem;
        }
        return { ...item, children: filteredChildren } as NavItem;
      }
      return item;
    })
    .filter(item => {
      if (item.isGroup) {
        return (item.children && item.children.length > 0) && filterByContext(item);
      }
      return filterByContext(item);
    });

  // Auto-expand groups with active children
  useState(() => {
    navigation.forEach(item => {
      if (item.isGroup && hasActiveChild(item.children)) {
        if (!expandedGroups.includes(item.name)) {
          setExpandedGroups(prev => [...prev, item.name]);
        }
      }
    });
  });

  // Load current user's editable info when opening edit modal
  useEffect(() => {
    const loadProfile = async () => {
      if (!user || !showEditModal) return;
      setProfileLoading(true);
      try {
        const u = await usersService.getUserById(user.uid);
        setDisplayName(u?.displayName || user.displayName || '');
        setPhoneNumber(u?.phoneNumber || '');
        // reset password fields each time dialog opens
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError(null);
      } catch (e) {
        // noop - keep defaults
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, [showEditModal, user]);

  return (
    <aside className="w-64 h-full bg-card hairline-r flex flex-col">
      <div className="p-6">
        <img
          src="/bevpass-logo-dark.svg"
          alt="BevPass"
          className="h-8 w-auto mb-1 select-none"
          draggable={false}
        />
        <p className="text-sm text-muted-foreground">Admin Dashboard v2</p>

        {/* Merchant Switcher moved from header */}
        <div className="mt-4">
          <MerchantSwitcher />
        </div>
      </div>

      <nav className="relative z-10 px-4 pb-4 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const isDisabled = !!(item.requiresMerchant && (isGlobalView || !currentMerchant));
            const isExpanded = isGroupExpanded(item.name);
            const hasActive = hasActiveChild(item.children);
            
            if (item.isGroup) {
              return (
                <li key={item.name}>
                  <button
                    onClick={() => toggleGroup(item.name)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isDisabled && 'opacity-50 pointer-events-none',
                      hasActive ? 'bg-muted text-foreground border-l-4 border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    disabled={isDisabled}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="flex-1 text-left">{item.name}</span>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {isExpanded && item.children && (
                    <ul className="mt-1 ml-4 pl-4 border-l border-muted space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childIsDisabled = !!(child.requiresMerchant && (isGlobalView || !currentMerchant));
                        
                        return (
                          <li key={child.name}>
                            <NavLink
                              to={child.href!}
                            className={({ isActive }) =>
                                cn(
                                  'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                                  childIsDisabled && 'opacity-50 pointer-events-none',
                                  isActive ? 'bg-muted text-foreground border-l-4 border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                )
                              }
                              onClick={(e) => {
                                if (childIsDisabled) {
                                  e.preventDefault();
                                }
                              }}
                            >
                              <ChildIcon className="h-4 w-4" />
                              <span className="flex-1">{child.name}</span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }
            
            return (
              <li key={item.name}>
                <NavLink
                  to={item.href!}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isDisabled && 'opacity-50 pointer-events-none',
                      isActive ? 'bg-muted text-foreground border-l-4 border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                  onClick={(e) => {
                    if (isDisabled) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1">{item.name}</span>
                  {item.requiresMerchant && (isGlobalView || !currentMerchant) && (
                    <Badge variant="outline" className="text-xs">
                      Merchant Required
                    </Badge>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {/* spacer maintained by flex-1; sign out moved to user panel */}
      </nav>

      {user && (
        <div className="p-4 hairline-t bg-card">
          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors"
                aria-label="Open account menu"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {user.email?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate">{user.displayName || user.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {user.role?.replace('_', ' ') || 'No Role'}
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Account</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowEditModal(true);
                }}
              >
                <Edit className="mr-2 h-4 w-4" /> Edit profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  signOut();
                }}
              >
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Edit Profile Modal */}
      {user && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Profile</DialogTitle>
              <DialogDescription>Update your display information.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  disabled={profileLoading || saving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="(###) ###-####"
                  disabled={profileLoading || saving}
                />
              </div>
              <div className="pt-2 border-t" />
              <div className="space-y-2">
                <Label className="font-semibold">Change password</Label>
                <p className="text-xs text-muted-foreground">Enter your current password and a new password to change it. Leave new password blank to keep your current password.</p>
                {passwordError && (
                  <p className="text-xs text-red-600">{passwordError}</p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current password (required to change password)</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    disabled={profileLoading || saving}
                    autoComplete="current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    disabled={profileLoading || saving}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    disabled={profileLoading || saving}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!user) return;
                    setSaving(true);
                    try {
                      setPasswordError(null);
                      // If user entered a new password, validate and update
                      if (newPassword || confirmPassword) {
                        if (!currentPassword) {
                          setPasswordError('Current password is required to change your password.');
                          return;
                        }
                        if (newPassword !== confirmPassword) {
                          setPasswordError('New passwords do not match.');
                          return;
                        }
                        if (newPassword.length < 8) {
                          setPasswordError('Password must be at least 8 characters.');
                          return;
                        }
                        const current = auth.currentUser;
                        if (!current || !user.email) throw new Error('No authenticated user');
                        // Always reauthenticate with current password before changing
                        const cred = EmailAuthProvider.credential(user.email, currentPassword);
                        await reauthenticateWithCredential(current, cred);
                        await firebaseUpdatePassword(current, newPassword);
                        toast.success('Password updated');
                      }
                      // Update profile fields
                      await usersService.updateUser(user.uid, {
                        displayName: displayName || undefined,
                        phoneNumber: phoneNumber || undefined,
                      });
                      setAuthUser({ ...user, displayName: displayName || null });
                      setShowEditModal(false);
                    } catch (e) {
                      // eslint-disable-next-line no-console
                      console.error('Failed to save profile', e);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={profileLoading || saving}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </aside>
  );
}
